import React from 'react';
import { Alert, Box, Button, FormControl, IconButton, InputLabel, MenuItem, Select, Slider, Typography } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { CheckCircle, CloudUpload, Crop } from '@mui/icons-material';
import { Icon } from '@iconify/react';
import type { StoreLocation } from '../../../types/services/auth';

type EditReceiptControlsPanelProps = {
  error: string | null;
  onClearError: () => void;
  locations: StoreLocation[];
  selectedLocationId: string;
  locationsLoading: boolean;
  locationsResolved: boolean;
  onLocationChange: (locationId: string) => void;
  selectedFile: File | null;
  previewUrl: string;
  editMode: 'tilt' | 'perspective' | 'free';
  freeCropPreviewBlob: Blob | null;
  modeSwitching: boolean;
  rotation: number;
  horizontalTilt: number;
  verticalTilt: number;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDrop: (event: React.DragEvent) => void;
  onDragOver: (event: React.DragEvent) => void;
  onFreeCropPreview: () => void;
  onFreeCropEdit: () => void;
  onFreeCropRevert: () => void;
  onRotationChange: (_: Event, value: number | number[]) => void;
  onHorizontalTiltChange: (_: Event, value: number | number[]) => void;
  onVerticalTiltChange: (_: Event, value: number | number[]) => void;
};

const EditReceiptControlsPanel: React.FC<EditReceiptControlsPanelProps> = React.memo(({
  error,
  onClearError,
  locations,
  selectedLocationId,
  locationsLoading,
  locationsResolved,
  onLocationChange,
  selectedFile,
  previewUrl,
  editMode,
  freeCropPreviewBlob,
  modeSwitching,
  rotation,
  horizontalTilt,
  verticalTilt,
  onFileSelect,
  onDrop,
  onDragOver,
  onFreeCropPreview,
  onFreeCropEdit,
  onFreeCropRevert,
  onRotationChange,
  onHorizontalTiltChange,
  onVerticalTiltChange,
}) => {
  return (
    <Box
      sx={{
        width: { xs: '100%', md: '45%' },
        flex: { xs: '1 1 auto', md: '0 0 45%' },
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={onClearError}>
          {error}
        </Alert>
      )}

      {locations.length > 1 && (
        <FormControl fullWidth required sx={{ mb: 2 }}>
          <InputLabel>Select Location</InputLabel>
          <Select
            value={selectedLocationId}
            label="Select Location"
            onChange={(event: SelectChangeEvent<string>) => onLocationChange(event.target.value)}
            disabled={locationsLoading}
          >
            <MenuItem value="" disabled>
              Select a location
            </MenuItem>
            {locations.map((location) => (
              <MenuItem key={location.id} value={location.id}>
                {location.location_name} ({location.location_key})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {!locationsLoading && !locationsResolved && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          A compatible store location is required. Upload is disabled.
        </Alert>
      )}

      <Box
        sx={{
          border: '2px dashed',
          borderColor: selectedFile ? 'success.main' : 'grey.300',
          borderRadius: 2,
          p: 1.5,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: '#2C6CA00A',
          '&:hover': {
            backgroundColor: '#2C6CA00A',
          },
          minHeight: '240px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        }}
        onClick={() => document.getElementById('edit-file-input')?.click()}
        onDrop={onDrop}
        onDragOver={onDragOver}
      >
        <input
          id="edit-file-input"
          type="file"
          accept="image/*"
          onChange={onFileSelect}
          style={{ display: 'none' }}
        />
        {selectedFile ? (
          <Box>
            <CheckCircle sx={{ fontSize: 48, color: 'success.main' }} />
            <Typography variant="body2" color="text.secondary" fontWeight="bold">
              {selectedFile.name} - {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" lineHeight={1.5}>
              Click to change file
            </Typography>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1}>
            <CloudUpload sx={{ fontSize: 48, color: 'text.secondary' }} />
            <Typography variant="h6" gutterBottom>
              Click or drag and drop an image file
            </Typography>
          </Box>
        )}
      </Box>

      {previewUrl && editMode === 'free' && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1, minHeight: 40 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={freeCropPreviewBlob ? onFreeCropEdit : onFreeCropPreview}
              disabled={modeSwitching}
              startIcon={<Crop />}
            >
              {freeCropPreviewBlob ? 'Edit' : 'Preview'}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={onFreeCropRevert}
              disabled={modeSwitching}
              size="medium"
              color="secondary"
            >
              Revert to Original
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ minHeight: 40 }}>
            {freeCropPreviewBlob
              ? 'Preview mode active. Click "Edit" to adjust corners again.'
              : 'Drag the four corners to adjust the crop area. Click "Preview" to see the result.'}
          </Typography>
        </Box>
      )}

      {previewUrl && editMode === 'perspective' && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="caption" color="text.secondary">
            Tilt (°): {rotation}°
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <IconButton
              size="small"
              onClick={() => onRotationChange({} as Event, rotation - 1)}
              disabled={rotation <= -180}
            >
              <Icon icon="lsicon:anticlockwise-90-filled" width="24" height="24" style={{ color: '#2C6CA0' }} />
            </IconButton>
            <Slider
              size="small"
              min={-180}
              max={180}
              step={1}
              value={rotation}
              onChange={onRotationChange}
              sx={{ flex: 1 }}
            />
            <IconButton
              size="small"
              onClick={() => onRotationChange({} as Event, rotation + 1)}
              disabled={rotation >= 180}
            >
              <Icon icon="lsicon:clockwise-90-filled" width="24" height="24" style={{ color: '#2C6CA0' }} />
            </IconButton>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Horizontal tilt: {horizontalTilt.toFixed(2)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <IconButton
              size="small"
              onClick={() => onHorizontalTiltChange({} as Event, horizontalTilt - 0.05)}
              disabled={horizontalTilt <= -1}
            >
              -
            </IconButton>
            <Slider
              size="small"
              min={-1}
              max={1}
              step={0.01}
              value={horizontalTilt}
              onChange={onHorizontalTiltChange}
              sx={{ flex: 1 }}
            />
            <IconButton
              size="small"
              onClick={() => onHorizontalTiltChange({} as Event, horizontalTilt + 0.05)}
              disabled={horizontalTilt >= 1}
            >
              +
            </IconButton>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Vertical tilt: {verticalTilt.toFixed(2)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => onVerticalTiltChange({} as Event, verticalTilt - 0.05)}
              disabled={verticalTilt <= -1}
            >
              -
            </IconButton>
            <Slider
              size="small"
              min={-1}
              max={1}
              step={0.01}
              value={verticalTilt}
              onChange={onVerticalTiltChange}
              sx={{ flex: 1 }}
            />
            <IconButton
              size="small"
              onClick={() => onVerticalTiltChange({} as Event, verticalTilt + 0.05)}
              disabled={verticalTilt >= 1}
            >
              +
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
});

EditReceiptControlsPanel.displayName = 'EditReceiptControlsPanel';

export default EditReceiptControlsPanel;
